var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '../vitesse-icon';
/**
 * <vitesse-secret-input> — a masked text field (passwords) with a reveal toggle
 * (the form-schema SECRET component) and a FILE-upload button (a key/token file's
 * text becomes the value — .pem/.key/.token/.txt/.json by default). Same look +
 * `change` contract as <vitesse-text-input>; the eye button flips masking.
 *
 *   <vitesse-secret-input .value=${pw} placeholder="Password"
 *       @change=${(e) => (pw = e.detail.value)}></vitesse-secret-input>
 *
 * `multiline` (the PEM component) swaps the <input> for a <textarea>, because a
 * certificate is many lines and a single-line control strips the newlines out of
 * a paste — the value that reached the server was one long line no PEM decoder
 * accepts. The textarea is masked with `-webkit-text-security` (Chromium, Safari;
 * Firefox has no equivalent and shows the text — the reveal toggle still works,
 * and the file picker is the same).
 *
 * Emits a `change` CustomEvent<{ value: string }> on every edit / file load.
 */
let VitesseSecretInput = class VitesseSecretInput extends LitElement {
    constructor() {
        super(...arguments);
        this.value = '';
        this.placeholder = '';
        this.disabled = false;
        /** Render a multi-line control (certificate / key material). */
        this.multiline = false;
        /** Accepted key-file extensions for the upload button. */
        this.accept = '.txt,.pem,.key,.token,.json,.env';
        this.revealed = false;
    }
    static { this.styles = css `
    :host {
      display: block;
    }
    .wrap {
      display: flex;
      align-items: stretch;
      width: 100%;
      box-sizing: border-box;
      border: 1px solid var(--border-color-light);
      border-radius: var(--radius, 6px);
      background: var(--bg-primary);
      overflow: hidden;
      transition: border-color 0.18s ease;
    }
    .wrap:focus-within {
      border-color: var(--blue);
    }
    input {
      flex: 1;
      min-width: 0;
      border: none;
      background: transparent;
      outline: none;
      color: var(--text-primary);
      font: inherit;
      font-size: 14px;
      padding: 10px 12px;
    }
    input::placeholder,
    textarea::placeholder {
      color: var(--text-secondary);
    }
    textarea {
      flex: 1;
      min-width: 0;
      border: none;
      background: transparent;
      outline: none;
      color: var(--text-primary);
      font: inherit;
      font-family: var(--font-mono, ui-monospace, monospace);
      font-size: 12px;
      line-height: 1.4;
      padding: 10px 12px;
      resize: vertical;
      min-height: 7.5em;
    }
    textarea.masked {
      -webkit-text-security: disc;
    }
    .eye {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      border: none;
      border-left: 1px solid var(--border-color-light);
      background: transparent;
      color: var(--text-secondary);
      cursor: pointer;
      transition: color 0.15s ease;
    }
    /* Action buttons highlight BLUE on hover (upload / reveal)… */
    .eye:hover {
      color: var(--blue);
    }
    /* …except when the eye means "stop viewing" (revealed → eye-off): that's a cancel → red. */
    .eye.off:hover {
      color: var(--red);
    }
    :host([disabled]) .wrap {
      opacity: 0.55;
    }
    :host([disabled]) input,
    :host([disabled]) textarea,
    :host([disabled]) .eye {
      cursor: not-allowed;
    }
    /* The action column sits at the top beside a tall textarea rather than
       stretching to its full height. */
    :host([multiline]) .wrap {
      align-items: flex-start;
    }
    :host([multiline]) .eye {
      height: 38px;
    }
  `; }
    emit(v) {
        this.value = v;
        this.dispatchEvent(new CustomEvent('change', { detail: { value: v }, bubbles: true, composed: true }));
    }
    async onFile(e) {
        const input = e.target;
        const file = input.files?.[0];
        input.value = ''; // allow re-picking the same file
        if (!file)
            return;
        // The file's TEXT is the secret (trimmed — key files commonly end in a newline).
        this.emit((await file.text()).trim());
    }
    render() {
        return html `<div class="wrap">
      ${this.multiline
            ? html `<textarea
            class=${this.revealed ? '' : 'masked'}
            placeholder=${this.placeholder}
            ?disabled=${this.disabled}
            spellcheck="false"
            autocomplete="off"
            .value=${this.value}
            @input=${(e) => this.emit(e.target.value)}
          ></textarea>`
            : html `<input
            type=${this.revealed ? 'text' : 'password'}
            placeholder=${this.placeholder}
            ?disabled=${this.disabled}
            .value=${this.value}
            @input=${(e) => this.emit(e.target.value)}
          />`}
      <button
        type="button"
        class="eye"
        ?disabled=${this.disabled}
        tabindex="-1"
        aria-label="Upload key file"
        @click=${() => this.renderRoot.querySelector('.file')?.click()}
      >
        <vitesse-icon name="upload" size="15"></vitesse-icon>
      </button>
      <button
        type="button"
        class="eye ${this.revealed ? 'off' : ''}"
        ?disabled=${this.disabled}
        tabindex="-1"
        aria-label="Toggle visibility"
        @click=${() => (this.revealed = !this.revealed)}
      >
        <vitesse-icon name=${this.revealed ? 'eye-off' : 'eye'} size="16"></vitesse-icon>
      </button>
      <input class="file" type="file" accept=${this.accept} hidden @change=${(e) => void this.onFile(e)} />
    </div>`;
    }
};
__decorate([
    property()
], VitesseSecretInput.prototype, "value", void 0);
__decorate([
    property()
], VitesseSecretInput.prototype, "placeholder", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], VitesseSecretInput.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], VitesseSecretInput.prototype, "multiline", void 0);
__decorate([
    property()
], VitesseSecretInput.prototype, "accept", void 0);
__decorate([
    state()
], VitesseSecretInput.prototype, "revealed", void 0);
VitesseSecretInput = __decorate([
    customElement('vitesse-secret-input')
], VitesseSecretInput);
export { VitesseSecretInput };
//# sourceMappingURL=vitesse-secret-input.js.map